import L from 'leaflet';
import { getSelectionStyle, getHoverStyle, createMusterStationPopupContent } from './map-styles.js';
import { fitToFeature } from './map-geometry.js';
import useEvacuationStore from '../../../stores/evacuation-store.js';
import useModelMappingStore from '../../../stores/model-mapping-store.js';

// ==================== MAP CLICK HANDLER ====================
export const setupMapClickHandler = (map, clickable, onMapClick) => {
  /**
   * Setup map click handler for custom map interactions
   * Allows external components to capture map click coordinates
   * 
   * @param {Object} map - Leaflet map instance
   * @param {boolean} clickable - Whether click handling is enabled
   * @param {Function} onMapClick - Callback function(latLng) on map click
   * @returns {Function} Cleanup function to remove event listener
   */
  if (!clickable || !onMapClick) return () => {};

  const handleClick = (e) => {
    const { lat, lng } = e.latlng;
    onMapClick({ lat, lng });
  };

  map.on('click', handleClick);

  // Return cleanup function
  return () => {
    map.off('click', handleClick);
  };
};

// ==================== EVACUATION MAP ====================
export const createTooltipContent = (properties) => {
  /**
   * Create tooltip content for feature hover
   * Displays basic feature information on mouseover
   * 
   * @param {Object} properties - Feature properties
   * @returns {string} HTML content for tooltip
   */

  const TAZ = properties.TAZ || properties.Taz || "N/A";
  const jwtName = properties.JTWName || properties.JTW_Name || "N/A";

  let content = '<div style="padding: 5px; min-width: 150px;">';

  // Only add properties that exist
  if (properties.TAZ || properties.Taz) {
    content += `<div style="margin-bottom: 5px;"><strong>TAZ:</strong> ${TAZ}</div>`;
  }

  if (properties.JTWName || properties.JTW_Name) {
    content += `<div><strong>JTW Name:</strong> ${jwtName}</div>`;
  }

  if (properties.ZONE_ID || properties.Zone_ID) {
    content += `<div><strong>Zone ID:</strong> ${properties.ZONE_ID || properties.Zone_ID
      }</div>`;
  }

  // Add a generic message if no properties were found
  if (content === '<div style="padding: 5px; min-width: 150px;">') {
    content += "<div>Feature selected</div>";
  }

  content += "</div>";
  return content;
};

export const createEnhancedPopupContent = (enhancedProperties) => {

  const { addresses, error } = enhancedProperties;
  let totalDwellings = 0;
  let totalPeople = 0;
  let totalVehicles = 0;

  if (addresses && Array.isArray(addresses)) {
    addresses.forEach((address) => {
      totalDwellings += parseFloat(address.Occupied_Dwell || 0);
      totalPeople += parseFloat(address.People || 0);
      totalVehicles += parseFloat(address.Evac_Veh || 0);
    });

    // Convert to integers after summing
    totalDwellings = Math.round(totalDwellings);
    totalPeople = Math.round(totalPeople);
    totalVehicles = Math.round(totalVehicles);
  }

  let content =
    '<div style="padding: 10px; min-width: 200px; max-width: 300px;">';

  if (error) {
    content += `<div style="color: red;"><strong>Error:</strong> ${error}</div>`;
  } else {
    content += `<div style="margin-bottom: 8px;"><strong>Total Dwellings:</strong> ${totalDwellings || "N/A"
      }</div>`;
    content += `<div style="margin-bottom: 8px;"><strong>Total People:</strong> ${totalPeople || "N/A"
      }</div>`;
    content += `<div style="margin-bottom: 8px;"><strong>Total Vehicles:</strong> ${totalVehicles || 0
      }</div>`;
  }
  content += "</div>";
  return content;
};

export const handlePolygonClick = (layer, feature, map, context) => {
  const store = useEvacuationStore.getState();
  const {
    selectionContext,
    setSelectionContext,
    clearSelectionContext,
    setFeatureRequest,
    clearFeatureRequest,
    clearDynamicLayersByRule
  } = store;

  // Deselect previous layer
  if (selectionContext.zoneLayer && selectionContext.zoneLayer !== layer) {
    const previousLayer = selectionContext.zoneLayer;
    previousLayer._isSelected = false;
    if (typeof previousLayer._getOriginalStyle === 'function') {
      previousLayer.setStyle(previousLayer._getOriginalStyle());
    }

    // Clear dynamic layers linked to old selection
    if (selectionContext.zoneId) {
      clearDynamicLayersByRule('deselect', { zoneId: selectionContext.zoneId });
    }
  }

  // Deselect current layer
  if (layer._isSelected) {
    context.setAppSidebarEndToggled(false);
    layer._isSelected = false;
    layer.setStyle(layer._getOriginalStyle());

    // Clear selection context
    clearSelectionContext();
    clearFeatureRequest();

    const zoneId = feature.properties.ZONE_ID || feature.properties.Zone_ID;
    if (zoneId) {
      clearDynamicLayersByRule('deselect', { zoneId });
    }

    console.log('📍 Layer deselected, context cleared');
  }
  // Select layer
  else {
    context.setAppSidebarEndToggled(true);
    layer._isSelected = true;
    const originalStyle = layer._getOriginalStyle();
    layer.setStyle(getSelectionStyle('area', originalStyle));

    const zoneId = feature.properties.ZONE_ID || feature.properties.Zone_ID;
    if (zoneId) {
      setSelectionContext({
        zoneId: zoneId,
        zoneLayer: layer,
        routeLayer: null, // Will be set when route loads
        musterStationLayer: null, // Will be set when route loads
        musterCoordinates: null, // Will be set when route loads
        musterName: null,
        musterAddress: null,
      });

      setFeatureRequest({
        zoneId: zoneId,
        params: {},
      });

      console.log('📌 Zone selected, context initialized');
    } else {
      console.warn('⚠️ No zone ID found in feature properties');
    }
  }
};

export const handleLineClick = (layer, feature, map, isSidebarOpen) => {
  fitToFeature(layer, map, isSidebarOpen);
};

export const handlePointClick = (layer, feature, map, isSidebarOpen) => {
  // Reset other selections
  map.eachLayer((mapLayer) => {
    if (mapLayer._isSelected && mapLayer !== layer) {
      mapLayer._isSelected = false;
      if (typeof mapLayer._getOriginalStyle === 'function') {
        mapLayer.setStyle(mapLayer._getOriginalStyle());
      } else if (typeof mapLayer._getOriginalIcon === 'function') {
        mapLayer.setIcon(mapLayer._getOriginalIcon());
      }
    }
  });

  // Toggle selection
  if (layer._isSelected) {
    layer._isSelected = false;

    // Reset style or icon
    if (typeof layer._getOriginalStyle === 'function') {
      layer.setStyle(layer._getOriginalStyle());
    } else if (typeof layer._getOriginalIcon === 'function') {
      layer.setIcon(layer._getOriginalIcon());
    }
  } else {
    layer._isSelected = true;

    // Apply selection style or icon
    if (typeof layer._getOriginalStyle === 'function') {
      const originalStyle = layer._getOriginalStyle();
      layer.setStyle(getSelectionStyle('point', originalStyle));
    } else if (typeof layer._getSelectedIcon === 'function') {
      layer.setIcon(layer._getSelectedIcon());
    }

    fitToFeature(layer, map, isSidebarOpen);
  }
};

export const handleFeatureMouseOver = (layer, layerType) => {
  if (!layer._isSelected) {
    if (typeof layer._getOriginalStyle === 'function') {
      const originalStyle = layer._getOriginalStyle();
      layer.setStyle(getHoverStyle(layerType, originalStyle));
    } else if (typeof layer._getSelectedIcon === 'function') {
      layer.setIcon(layer._getSelectedIcon());
    }
  }
};

export const handleFeatureMouseOut = (layer) => {
  if (!layer._isSelected) {
    if (typeof layer._getOriginalStyle === 'function') {
      layer.setStyle(layer._getOriginalStyle());
    } else if (typeof layer._getOriginalIcon === 'function') {
      layer.setIcon(layer._getOriginalIcon());
    }
  }
};

export const setupEvacuationPolygonInteractions = (layer, feature, map, layerKey, context) => {

  // Bind tooltip
  layer.bindTooltip(createTooltipContent(feature.properties), {
    permanent: false,
    direction: 'top',
    offset: [0, -10],
    className: 'polygon-hover-tooltip',
  });

  // Click event
  layer.on('click', function (e) {
    handlePolygonClick(this, feature, map, context);
  });

  // Hover events
  layer.on('mouseover', function () {
    handleFeatureMouseOver(this, 'area');
  });

  layer.on('mouseout', function () {
    handleFeatureMouseOut(this);
    this.closePopup();
  });
};

export const setupPointInteractions = (layer, feature, map, isSidebarOpen, isCustomIcon = false) => {
  layer.on('click', function () {
    handlePointClick(this, feature, map, isSidebarOpen);
  });

  if (!isCustomIcon) {
    layer.on('mouseover', function () {
      handleFeatureMouseOver(this, 'point');
    });

    layer.on('mouseout', function () {
      handleFeatureMouseOut(this);
    });
  } else {
    // Custom icon hover (just change icon)
    layer.on('mouseover', function () {
      if (!this._isSelected && typeof this._getSelectedIcon === 'function') {
        this.setIcon(this._getSelectedIcon());
      }
    });

    layer.on('mouseout', function () {
      if (!this._isSelected && typeof this._getOriginalIcon === 'function') {
        this.setIcon(this._getOriginalIcon());
      }
    });
  }
};

export const setupMusterStationInteractions = (layer, feature, map, isSidebarOpen) => {
  setupPointInteractions(layer, feature, map, isSidebarOpen, true);
  setupMusterStationPopup(layer, feature);
};

// Hover-only capacity donut popup, no click/selection - used on pages (e.g. report) that don't support zone selection
export const setupMusterStationPopup = (layer, feature) => {
  layer.bindPopup(createMusterStationPopupContent(feature.properties), {
    className: 'muster-station-popup',
    closeButton: false,
  });

  layer.on('mouseover', function () {
    this.openPopup();
  });

  layer.on('mouseout', function () {
    this.closePopup();
  });
};

export const setupLineInteractions = (layer, feature, map, isSidebarOpen) => {
  layer.on('click', function () {
    handleLineClick(this, feature, map, isSidebarOpen);
  });

  layer.on('mouseover', function () {
    handleFeatureMouseOver(this, 'line');
  });

  layer.on('mouseout', function () {
    handleFeatureMouseOut(this);
  });
};


// ==================== MODELLING MAP ====================
export const handleTAZClick = (layer, feature, map, context) => {
  const store = useModelMappingStore.getState();
  const { setSelectedFeature, clearSelectedFeature } = store;

  // Deselect all other layers on map
  map.eachLayer((mapLayer) => {
    if (mapLayer._isSelected && mapLayer !== layer) {
      mapLayer._isSelected = false;
      if (typeof mapLayer._getOriginalStyle === 'function') {
        mapLayer.setStyle(mapLayer._getOriginalStyle());
      }
    }
  });

  // Toggle selection for clicked layer
  if (layer._isSelected) {
    // Deselect
    context.setAppSidebarEndToggled(false);
    layer._isSelected = false;
    layer.setStyle(layer._getOriginalStyle());
    clearSelectedFeature();

    console.log('📍 TAZ deselected');
  } else {
    // Select
    context.setAppSidebarEndToggled(true);
    layer._isSelected = true;
    const originalStyle = layer._getOriginalStyle();
    layer.setStyle(getSelectionStyle('area', originalStyle));

    // Store feature in model-mapping store
    const featureData = {
      ...feature.properties,
      _layerFilename: `${feature.properties._layerFilename}.geojson` || 'CRD_TAZ_2025_v2.geojson',
      _timestamp: Date.now(),
    };

    setSelectedFeature(featureData);

    console.log('📌 TAZ selected:', feature.properties.TAZ);
  }
};

export const setupModelMappingPolygonInteractions = (layer, feature, map, layerKey, context) => {
  // Bind tooltip
  layer.bindTooltip(createTooltipContent(feature.properties), {
    permanent: false,
    direction: 'top',
    offset: [0, -10],
    className: 'polygon-hover-tooltip',
  });

  // Click event - TAZ handler
  layer.on('click', function (e) {
    handleTAZClick(this, feature, map, context);
  });

  // Hover events
  layer.on('mouseover', function () {
    handleFeatureMouseOver(this, 'area');
  });

  layer.on('mouseout', function () {
    handleFeatureMouseOut(this);
    this.closePopup();
  });
};